import type { WebGLRenderer, PerspectiveCamera } from 'three';
import type { QualitySettings, PostPipeline, MaterialFactory } from '../render/types';
import { planResize } from './resizeDecision';

export interface ResizeState {
  cssWidth: number;
  cssHeight: number;
  renderWidth: number;
  renderHeight: number;
}

/* WHY no `canvas.clientWidth || window.innerWidth` fallback: that fallback substituted a
   plausible-looking but unrelated number the moment the canvas measured 0, so a hidden or
   not-yet-laid-out container never surfaced as "not ready" -- it silently sized the renderer to
   the window instead, which could mismatch the canvas's real box once revealed, or, if the window
   itself reported 0 too (an embedding whose viewport starts at zero size), still resolve to 0 and
   leave the renderer stuck there forever, since nothing retried. Returning null instead lets the
   caller skip sizing and skip drawing, and watchCanvasResize below is what retries once the
   canvas's own box is real. */
export function handleResize(
  renderer: WebGLRenderer,
  camera: PerspectiveCamera,
  pipeline: PostPipeline,
  materials: MaterialFactory,
  quality: QualitySettings
): ResizeState | null {
  const canvas = renderer.domElement;
  const plan = planResize(
    { width: canvas.clientWidth, height: canvas.clientHeight },
    window.devicePixelRatio,
    quality
  );
  if (!plan) return null;

  /* The stylesheet sizes the canvas to the viewport. Writing an inline pixel size here would pin
     clientWidth to the first measurement and every later resize would be ignored. */
  renderer.setSize(plan.renderWidth, plan.renderHeight, false);

  camera.aspect = plan.cssWidth / plan.cssHeight;
  camera.updateProjectionMatrix();

  pipeline.setSize(plan.renderWidth, plan.renderHeight);
  materials.setRenderHeight(plan.renderHeight);
  materials.setPaintRadius(quality.kuwaharaRadius);

  return {
    cssWidth: plan.cssWidth,
    cssHeight: plan.cssHeight,
    renderWidth: plan.renderWidth,
    renderHeight: plan.renderHeight,
  };
}

/* WHY ResizeObserver instead of a window `resize` listener: it reports the canvas's own
   content-box size and, per spec, delivers an initial notification for every newly observed
   target -- including one that is laid out for the first time after observation starts, or one
   that starts at zero and only later gets a box. A container becoming visible, or an ancestor's
   display changing, resizes that content box without ever firing `resize` on window, which is
   exactly the load-while-hidden case this replaces. */
export function watchCanvasResize(
  canvas: HTMLCanvasElement,
  win: Window,
  onResize: () => void
): () => void {
  const observer = new ResizeObserver(() => {
    onResize();
  });
  observer.observe(canvas);
  const stopPixelRatioWatch = watchPixelRatio(win, onResize);
  return () => {
    observer.disconnect();
    stopPixelRatioWatch();
  };
}

/* WHY a media query besides the observer: a window moved to a screen of another pixel ratio keeps
   the canvas's CSS box, so the observer stays silent while the render size (renderSize) changes.
   A resolution query matches only the ratio it was made for, so each change re-arms it for the
   new one. */
function watchPixelRatio(win: Window, onChange: () => void): () => void {
  const currentRatio = (): MediaQueryList =>
    win.matchMedia(`(resolution: ${String(win.devicePixelRatio)}dppx)`);
  let query = currentRatio();
  const changed = (): void => {
    query.removeEventListener('change', changed);
    query = currentRatio();
    query.addEventListener('change', changed);
    onChange();
  };
  query.addEventListener('change', changed);
  return () => {
    query.removeEventListener('change', changed);
  };
}
